'use client';

import { API_ROUTES, useApiMutation } from '@shared/api-utils';
import { useSignOut } from '@shared/auth';
import { Button, Label, RadioGroup, RadioGroupItem } from '@shared/components/shadcn';
import { type SaferGamblingSelfExclusionPeriod, getSiteConfig } from '@shared/config';
import { createDebug } from '@shared/debug';
import { Spinner } from '@shared/icons';
import { SelfExclusion } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { Checkbox } from '../../../../../form-controls/Checkbox/Checkbox';
import { FieldMessage } from '../../../../../form-controls/FieldMessage/FieldMessage';
import { toast } from '../../../../../Toast/Toast';
import type { SaferGamblingPeriodRequest, SelfExclusionPeriod } from '../../../types';
import { completeBreakToolSubmission } from '../../../utils/breakToolConfirmation';
import { formatIsoDurationForTracking } from '../../../utils/formatTimeOffTracking';
import type { ExclusionFormData } from '../SelfExclusionSheet';

const getExclusionFormSchema = (t: (key: string) => string, periods: readonly [SaferGamblingSelfExclusionPeriod, ...SaferGamblingSelfExclusionPeriod[]]) =>
  z.object({
    period: z.string().refine((value): value is SaferGamblingSelfExclusionPeriod => value !== '' && periods.includes(value as SaferGamblingSelfExclusionPeriod), {
      message: t('validation'),
    }),
    confirmExclusion: z.boolean().refine(val => val === true, {
      message: t('confirmation.validation'),
    }),
  });

const ExclusionStep = () => {
  const t = useTranslations('my_account.safer_gambling.self_exclusion');
  const form = useFormContext<ExclusionFormData>();
  const router = useRouter();
  const signOut = useSignOut();
  const debug = createDebug({ isEnabled: false });
  const { selfExclusionPeriods: periods, disableBreakToolConfirmUntilValid } = getSiteConfig().SAFER_GAMBLING;

  const periodOptions = periods.map(value => ({
    value,
    label: t(`periods.${value}`),
  }));

  const selectedPeriod = form.watch('period');
  const confirmExclusion = form.watch('confirmExclusion');
  const postSelfExclusion = useApiMutation<void, SaferGamblingPeriodRequest>(API_ROUTES.MY_ACCOUNT.SELF_EXCLUSION.POST, 'POST');
  const isButtonLoading = postSelfExclusion.isPending;
  const isConfirmDisabled = isButtonLoading || (disableBreakToolConfirmUntilValid && (!selectedPeriod || !confirmExclusion));

  const handleContinue = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    try {
      const period = form.getValues('period');

      if (!period) {
        return;
      }

      postSelfExclusion.mutate(
        { period },
        {
          onSuccess: async () => {
            await completeBreakToolSubmission({
              breakType: 'self_exclusion',
              period,
              signOut,
              router,
            });
          },
          onError: () => {
            toast(t('add_error_toast'), { type: 'error' });
          },
        }
      );
    } catch (error) {
      debug.forceError('Error during exclusion submission:', error);
      toast(t('add_error_toast'), { type: 'error' });
    }
  };

  return (
    <form className="flex flex-col gap-8 p-6" data-testid="self-exclusion-step">
      <div className="flex flex-col gap-2 items-start justify-start w-full">
        <SelfExclusion size="extra-extra-large" className="mb-5" data-testid="self-exclusion-icon" />
        <h4>{t('title')}</h4>
        <div className="flex flex-col gap-2">
          <p>{t('description')}</p>
          {t('description_2') ? <p>{t('description_2')}</p> : null}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <RadioGroup
          value={selectedPeriod ?? ''}
          onValueChange={value => form.setValue('period', value as SelfExclusionPeriod, { shouldValidate: true })}
          className="flex flex-col gap-4 w-full"
          data-track-name="Period"
        >
          {periodOptions.map(option => (
            <div key={option.value} className="flex flex-row gap-3 h-10 items-center justify-start w-full">
              <RadioGroupItem
                className="size-7"
                value={option.value}
                id={`period-${option.value}`}
                data-testid={`period-${option.value}`}
                {...(selectedPeriod === option.value ? { 'data-track-period': formatIsoDurationForTracking(option.value) } : {})}
              />
              <Label className="paragraph-explanatory hover:text-inherit" htmlFor={`period-${option.value}`}>
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <FieldMessage message={form.formState.errors.period?.message?.toString()} messageType={form.formState.errors.period ? 'error' : undefined} />
      </div>

      <Checkbox
        id="confirmExclusion"
        checked={confirmExclusion}
        onCheckedChange={(checked: boolean) => {
          form.setValue('confirmExclusion', checked, { shouldValidate: true });
        }}
        label={t('confirmation.checkbox_label')}
        helpMessageType={form.formState.errors.confirmExclusion ? 'error' : undefined}
        helpMessage={form.formState.errors.confirmExclusion?.message?.toString()}
        trackingProps={{
          'data-track-name': 'Confirmation',
          'data-track-helper': 'Confirmation',
        }}
      />

      <div data-track-container="Actions">
        <Button className="w-full" onClick={handleContinue} disabled={isConfirmDisabled} data-track-name="Confirm" data-testid="self-exclusion-confirm-button" type="button">
          {isButtonLoading ? <Spinner size="small" className="animate-spin w-5" aria-label={t('aria.loading')} /> : t('continue')}
        </Button>
      </div>
    </form>
  );
};

export { ExclusionStep, getExclusionFormSchema };
